/** 用户端创建市场：选项与流动性分配 */

export const LIQUIDITY_PRESETS = [10, 50, 100, 200, 500, 1000];

export const MIN_LIQUIDITY = LIQUIDITY_PRESETS[0];

export const BINARY_OUTCOME_LABELS = ['Yes', 'No'];

export const MAX_OUTCOME_SHARE = 99.98;
export const MAX_OUTCOME_SHARE_SUM = 100;

/** 分配总额与已分配之差的容差（仅用于浮点比较，单位：美元） */
export const LIQUIDITY_ALLOCATION_EPSILON = 0.0001;

export const OUTCOME_DOT_COLORS = [
  '#2563eb',
  '#e05252',
  '#ea580c',
  '#64748b',
  '#4338ca',
  '#9333ea',
  '#0891b2',
];

export function roundShare(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.round(num * 100) / 100;
}

export function roundAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.round(num * 100) / 100;
}

/**
 * 将总额均分为 count 份（单位：美分整数运算），余数全部给最后一项，保证 sum === roundAmount(total)。
 * @param {number} total
 * @param {number} count
 * @returns {number[]}
 */
export function splitAmountEvenly(total, count) {
  const normalizedTotal = roundAmount(total);
  if (count <= 0) return [];
  if (normalizedTotal <= 0) return Array.from({ length: count }, () => 0);

  const totalCents = Math.round(normalizedTotal * 100);
  const baseCents = Math.floor(totalCents / count);
  const amounts = Array.from({ length: count }, () => baseCents);
  const remainderCents = totalCents - baseCents * count;
  amounts[count - 1] += remainderCents;

  return amounts.map((cents) => cents / 100);
}

/** 已分配金额是否与目标总额一致（按美分比较） */
export function isLiquidityFullyAllocated(allocated, total) {
  const targetCents = Math.round(roundAmount(total) * 100);
  const allocatedCents = Math.round(roundAmount(allocated) * 100);
  return allocatedCents >= targetCents;
}

/** 剩余未分配金额（非负，精确到美分） */
export function remainingLiquidityAmount(total, allocated) {
  const targetCents = Math.round(roundAmount(total) * 100);
  const allocatedCents = Math.round(roundAmount(allocated) * 100);
  return Math.max(0, targetCents - allocatedCents) / 100;
}

/** 解析金额输入，仅保留数字与最多两位小数 */
export function parseMoneyInput(value, { maxDecimals = 2 } = {}) {
  if (value == null || value === '') return null;
  const raw = String(value).trim().replace(/,/g, '');
  if (!raw) return null;
  const cleaned = raw.replace(/[^\d.]/g, '');
  if (!cleaned) return null;
  const [intPart = '', ...decimalParts] = cleaned.split('.');
  const decimals = decimalParts.join('').slice(0, maxDecimals);
  const normalized = decimals ? `${intPart || '0'}.${decimals}` : intPart;
  const num = Number(normalized);
  if (!Number.isFinite(num) || num < 0) return null;
  return roundAmount(num);
}

export function clampMoneyInput(value, { min = 0, max = Number.POSITIVE_INFINITY } = {}) {
  const parsed = parseMoneyInput(value);
  if (parsed == null) return null;
  return roundAmount(Math.min(max, Math.max(min, parsed)));
}

/** 输入过程中保留合法数字字符，最多两位小数 */
export function sanitizeMoneyTyping(value, { maxDecimals = 2 } = {}) {
  const raw = String(value ?? '');
  if (!raw) return '';
  let cleaned = raw.replace(/,/g, '').replace(/[^\d.]/g, '');
  const dotIndex = cleaned.indexOf('.');
  if (dotIndex === -1) return cleaned;
  const intPart = cleaned.slice(0, dotIndex);
  const decPart = cleaned
    .slice(dotIndex + 1)
    .replace(/\./g, '')
    .slice(0, maxDecimals);
  if (!decPart && raw.endsWith('.')) return `${intPart}.`;
  return decPart ? `${intPart}.${decPart}` : intPart;
}

export function normalizeShareValue(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  const share = num > 0 && num <= 1 ? num * 100 : num;
  return Math.min(MAX_OUTCOME_SHARE, Math.max(0, roundShare(share)));
}

export function amountToShare(amount, totalLiquidity) {
  const total = Number(totalLiquidity);
  if (!total || total <= 0) return 0;
  return normalizeShareValue((Number(amount) / total) * 100);
}

export function shareToAmount(share, totalLiquidity) {
  const total = Number(totalLiquidity);
  if (!total || total <= 0) return 0;
  return roundAmount((normalizeShareValue(share) / 100) * total);
}

export function sumOutcomeAmounts(outcomes, excludeIndex = -1) {
  if (!Array.isArray(outcomes)) return 0;
  return roundAmount(
    outcomes.reduce((total, item, index) => {
      if (index === excludeIndex) return total;
      return total + roundAmount(item?.amount);
    }, 0)
  );
}

export function getMaxOutcomeAmount(outcomes, outcomeIndex, totalLiquidity) {
  const total = Number(totalLiquidity);
  if (!total || total <= 0) return 0;
  const othersSum = sumOutcomeAmounts(outcomes, outcomeIndex);
  const maxBySum = roundAmount(total - othersSum);
  const maxByShare = roundAmount((total * MAX_OUTCOME_SHARE) / 100);
  return Math.max(0, Math.min(maxByShare, maxBySum));
}

export function clampOutcomeAmount(amount, otherAmountsSum, totalLiquidity) {
  const total = Number(totalLiquidity);
  if (!total || total <= 0) return 0;
  const maxBySum = roundAmount(total - otherAmountsSum);
  const maxByShare = roundAmount((total * MAX_OUTCOME_SHARE) / 100);
  const max = Math.max(0, Math.min(maxByShare, maxBySum));
  return Math.min(max, Math.max(0, roundAmount(amount)));
}

export function createDefaultOutcomeShares(count) {
  if (count <= 0) return [];
  const base = Math.floor((100 / count) * 100) / 100;
  const shares = Array.from({ length: count }, () => base);
  const sumExceptLast = shares.slice(0, -1).reduce((total, item) => total + item, 0);
  shares[count - 1] = roundShare(100 - sumExceptLast);
  return shares;
}

export function createEmptyOutcome(label = '', amount = 0, share = 0) {
  return { label, amount: roundAmount(amount), share: normalizeShareValue(share) };
}

export function createDefaultBinaryOutcomes(totalLiquidity = 0) {
  const total = roundAmount(totalLiquidity);
  if (!total) {
    return BINARY_OUTCOME_LABELS.map((label) => createEmptyOutcome(label, 0, 0));
  }
  const amounts = splitAmountEvenly(total, BINARY_OUTCOME_LABELS.length);
  return BINARY_OUTCOME_LABELS.map((label, index) =>
    createEmptyOutcome(label, amounts[index], amountToShare(amounts[index], total))
  );
}

export function createEmptyMarketHandicap({ question = '', outcomes } = {}) {
  return {
    question: String(question ?? ''),
    outcomes: Array.isArray(outcomes)
      ? outcomes.map((row) => ({ ...row }))
      : createDefaultBinaryOutcomes(0),
  };
}

export function sumAllMarketOutcomeAmounts(markets, exclude = null) {
  if (!Array.isArray(markets)) return 0;
  return roundAmount(
    markets.reduce((total, market, marketIndex) => {
      const outcomes = Array.isArray(market?.outcomes) ? market.outcomes : [];
      return (
        total +
        outcomes.reduce((sum, outcome, outcomeIndex) => {
          if (exclude?.marketIndex === marketIndex && exclude?.outcomeIndex === outcomeIndex) {
            return sum;
          }
          return sum + roundAmount(outcome?.amount);
        }, 0)
      );
    }, 0)
  );
}

export function getMaxOutcomeAmountGlobal(markets, marketIndex, outcomeIndex, totalLiquidity) {
  const total = Number(totalLiquidity);
  if (!total || total <= 0) return 0;
  const othersSum = sumAllMarketOutcomeAmounts(markets, { marketIndex, outcomeIndex });
  const maxBySum = roundAmount(total - othersSum);
  const maxByShare = roundAmount((total * MAX_OUTCOME_SHARE) / 100);
  return Math.max(0, Math.min(maxByShare, maxBySum));
}

export function evenlyDistributeAllMarkets(markets, totalLiquidity) {
  const slots = [];
  (Array.isArray(markets) ? markets : []).forEach((market, marketIndex) => {
    (Array.isArray(market?.outcomes) ? market.outcomes : []).forEach((_, outcomeIndex) => {
      slots.push({ marketIndex, outcomeIndex });
    });
  });
  if (!slots.length) return [];

  const total = roundAmount(totalLiquidity);
  const amounts = splitAmountEvenly(total, slots.length);
  const nextMarkets = markets.map((market) => ({
    ...market,
    outcomes: (market.outcomes ?? []).map((row) => ({ ...row, amount: 0, share: 0 })),
  }));

  slots.forEach((slot, index) => {
    const amount = amounts[index];
    const row = nextMarkets[slot.marketIndex].outcomes[slot.outcomeIndex];
    nextMarkets[slot.marketIndex].outcomes[slot.outcomeIndex] = {
      ...row,
      amount,
      share: amountToShare(amount, total),
    };
  });

  return nextMarkets;
}

export function clearAllMarketOutcomeAmounts(markets) {
  return (Array.isArray(markets) ? markets : []).map((market) => ({
    ...market,
    outcomes: clearOutcomeAmounts(market.outcomes),
  }));
}

export function sumMarketOutcomeAmounts(market, excludeIndex = -1) {
  const outcomes = Array.isArray(market?.outcomes) ? market.outcomes : [];
  return sumOutcomeAmounts(outcomes, excludeIndex);
}

export function isMarketLiquidityAllocated(market, minAmount = 0.01) {
  return sumMarketOutcomeAmounts(market) >= minAmount;
}

export function getUnallocatedMarketIndexes(markets, minAmount = 0.01) {
  if (!Array.isArray(markets)) return [];
  return markets.reduce((indexes, market, index) => {
    if (!isMarketLiquidityAllocated(market, minAmount)) indexes.push(index);
    return indexes;
  }, []);
}

export function countAllOutcomeSlots(markets) {
  if (!Array.isArray(markets)) return 0;
  return markets.reduce((count, market) => count + (market?.outcomes?.length ?? 0), 0);
}

export function getMarketBudget(markets, marketIndex, totalLiquidity) {
  const total = roundAmount(totalLiquidity);
  if (!total || !Array.isArray(markets) || !markets.length) return 0;
  const market = markets[marketIndex];
  if (!market) return 0;

  const marketAmounts = splitAmountEvenly(total, markets.length);
  const theoretical = marketAmounts[marketIndex] ?? 0;
  const current = sumMarketOutcomeAmounts(market);
  return Math.max(theoretical, current);
}

export function getOutcomeAmountCap(markets, marketIndex, outcomeIndex, totalLiquidity) {
  const total = roundAmount(totalLiquidity);
  if (!total) return Number.POSITIVE_INFINITY;

  const current = roundAmount(markets?.[marketIndex]?.outcomes?.[outcomeIndex]?.amount ?? 0);
  const globalMax = getMaxOutcomeAmountGlobal(markets, marketIndex, outcomeIndex, total);
  return Math.max(current, globalMax);
}

export function applyOutcomeAmount(markets, marketIndex, outcomeIndex, amount, totalLiquidity) {
  const total = roundAmount(totalLiquidity);
  const nextAmount = roundAmount(amount);
  const share = total > 0 ? amountToShare(nextAmount, total) : 0;

  return markets.map((market, mi) => {
    if (mi !== marketIndex) return market;
    return {
      ...market,
      outcomes: (market.outcomes ?? []).map((row, oi) => {
        if (oi !== outcomeIndex) return row;
        return { ...row, amount: nextAmount, share };
      }),
    };
  });
}

/** 先按选项均分总额，再在各选项内均分 Yes/No 或多结果；金额层最后一项补余 */
export function evenlyDistributeHierarchical(markets, totalLiquidity) {
  const total = roundAmount(totalLiquidity);
  if (!total || !Array.isArray(markets) || !markets.length) {
    return clearAllMarketOutcomeAmounts(markets);
  }

  const marketAmounts = splitAmountEvenly(total, markets.length);

  return markets.map((market, marketIndex) => {
    const outcomes = market.outcomes ?? [];
    const marketBudget = marketAmounts[marketIndex] ?? 0;
    const outcomeAmounts = splitAmountEvenly(marketBudget, Math.max(outcomes.length, 1));

    return {
      ...market,
      outcomes: outcomes.map((row, outcomeIndex) => {
        const amount = outcomeAmounts[outcomeIndex] ?? 0;
        return {
          ...row,
          amount,
          share: amountToShare(amount, total),
        };
      }),
    };
  });
}

/** 仅在当前选项内均分其已分配额度；若未分配则先按全局层级切分该选项配额 */
export function evenlyDistributeCurrentMarket(markets, marketIndex, totalLiquidity) {
  const total = roundAmount(totalLiquidity);
  const market = markets[marketIndex];
  if (!market || !total) return markets;

  const outcomes = market.outcomes ?? [];
  if (!outcomes.length) return markets;

  const marketBudget = getMarketBudget(markets, marketIndex, total);
  const outcomeAmounts = splitAmountEvenly(marketBudget, outcomes.length);

  return markets.map((item, mi) => {
    if (mi !== marketIndex) return item;
    return {
      ...item,
      outcomes: outcomes.map((row, outcomeIndex) => {
        const amount = outcomeAmounts[outcomeIndex] ?? 0;
        return {
          ...row,
          amount,
          share: amountToShare(amount, total),
        };
      }),
    };
  });
}

export function recalcAllMarketsProportional(markets, prevTotal, nextTotal) {
  const prev = roundAmount(prevTotal);
  const next = roundAmount(nextTotal);
  if (!prev || !next || prev === next) return markets;

  const slots = [];
  (Array.isArray(markets) ? markets : []).forEach((market, marketIndex) => {
    (Array.isArray(market?.outcomes) ? market.outcomes : []).forEach((row, outcomeIndex) => {
      slots.push({ marketIndex, outcomeIndex, prevAmount: roundAmount(row?.amount) });
    });
  });
  if (!slots.length) return markets;

  const amounts = slots.map(({ prevAmount }) => roundAmount((prevAmount / prev) * next));
  const sumExceptLast = amounts.slice(0, -1).reduce((sum, value) => sum + value, 0);
  amounts[amounts.length - 1] = roundAmount(next - sumExceptLast);

  const nextMarkets = markets.map((market) => ({
    ...market,
    outcomes: (market.outcomes ?? []).map((row) => ({ ...row })),
  }));

  slots.forEach((slot, index) => {
    const amount = amounts[index];
    const row = nextMarkets[slot.marketIndex].outcomes[slot.outcomeIndex];
    nextMarkets[slot.marketIndex].outcomes[slot.outcomeIndex] = {
      ...row,
      amount,
      share: amountToShare(amount, next),
    };
  });

  return nextMarkets;
}

export function recalcAllMarketsFromShares(markets, totalLiquidity) {
  return (Array.isArray(markets) ? markets : []).map((market) => ({
    ...market,
    outcomes: recalcOutcomeAmountsFromShares(market.outcomes, totalLiquidity),
  }));
}

export function marketOutcomePrices(outcomes) {
  const rows = Array.isArray(outcomes) ? outcomes : [];
  const marketTotal = sumOutcomeAmounts(rows);
  if (!marketTotal) {
    const shares = createDefaultOutcomeShares(rows.length || 2);
    return shares;
  }
  return rows.map((row) => roundShare((roundAmount(row.amount) / marketTotal) * 100));
}

export function redistributeOutcomeShares(outcomes) {
  if (!Array.isArray(outcomes) || !outcomes.length) return [];
  return outcomes.map((item) => ({
    ...item,
    amount: roundAmount(item?.amount),
    share: 0,
  }));
}

export function clearOutcomeAmounts(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    amount: 0,
    share: 0,
  }));
}

export function createDefaultMultiOutcomes(count = 2, totalLiquidity = 0) {
  const total = roundAmount(totalLiquidity);
  if (!total) {
    return Array.from({ length: count }, () => createEmptyOutcome('', 0, 0));
  }
  const amounts = splitAmountEvenly(total, count);
  return Array.from({ length: count }, (_, index) =>
    createEmptyOutcome('', amounts[index], amountToShare(amounts[index], total))
  );
}

export function evenlyDistributeOutcomes(rows, totalLiquidity) {
  const labels = (Array.isArray(rows) ? rows : []).map((row) => String(row?.label ?? ''));
  const count = Math.max(labels.length, 2);
  const labelList =
    labels.length >= 2
      ? labels
      : [...labels, ...Array.from({ length: 2 - labels.length }, () => '')];
  const total = roundAmount(totalLiquidity);
  const amounts = splitAmountEvenly(total, count);
  return labelList.map((label, index) =>
    createEmptyOutcome(label, amounts[index], amountToShare(amounts[index], total))
  );
}

export function recalcOutcomeAmountsFromShares(rows, totalLiquidity) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    amount: shareToAmount(row.share, totalLiquidity),
  }));
}

export function serializeOutcomes(options) {
  const list = (Array.isArray(options) ? options : [])
    .map((opt) => String(opt?.label ?? opt ?? '').trim())
    .filter(Boolean);
  return list.length ? JSON.stringify(list) : '';
}

export function serializeOutcomesLiquidity(options) {
  const pairs = (Array.isArray(options) ? options : [])
    .map((opt) => ({
      label: String(opt?.label ?? opt ?? '').trim(),
      amount: roundAmount(opt?.amount ?? 0),
    }))
    .filter((item) => item.label);

  if (!pairs.length) return '';
  return JSON.stringify(pairs.map((item) => item.amount));
}

export function serializeOutcomePrices(shares) {
  if (!Array.isArray(shares) || !shares.length) return '';
  // UI shares are percent (50/50); API outcomePrices are probabilities in [0,1].
  return JSON.stringify(
    shares.map((share) => {
      const n = Number(share) || 0;
      const probability = n > 1 ? n / 100 : n;
      return Math.round(probability * 10000) / 10000;
    })
  );
}
